// ============================================================================
// Derived display state (spec §3.1)
// ----------------------------------------------------------------------------
// A room carries three fields — occupancy, housekeeping, service overlay — but
// every dashboard surface needs one primary reading per room. That reading is
// computed here and never stored, so the summary bar, the filter chips, the
// card frame and the floor-plan cell cannot drift apart.
// ============================================================================
/**
 * The seven values the summary bar and filter chips work in (§4.2, §4.3):
 * the derived state with "needs housekeeping" expanded into its three
 * housekeeping values.
 */
export const GRMS_DISPLAY_STATES = [
    'occupied',
    'ready',
    'dirty',
    'cleaning',
    'to_inspect',
    'out_of_order',
    'out_of_service',
];
/**
 * The five values the card frame and floor-plan cell work in (§5.6), where the
 * three housekeeping details collapse into one "needs housekeeping" frame and
 * the housekeeping chip carries the detail.
 */
export const GRMS_FRAME_STATES = [
    'ready',
    'occupied',
    'needs_housekeeping',
    'out_of_order',
    'out_of_service',
];
/**
 * Merge live MQTT state over REST state, one axis at a time. The two MQTT
 * topics carry different axes (§12.1), so a room whose housekeeping message
 * arrived must not lose the occupancy it already had, and vice versa.
 *
 * Defaults match the column defaults in `room_grms` (§10.1): a non-hospitality
 * room reports all three as NULL and reads as vacant, ready, in service.
 */
export function resolveGrmsRoomAxes(room, liveState) {
    return {
        occupancyStatus: liveState?.occupancyStatus ?? room.occupancyStatus ?? 'vacant',
        housekeepingStatus: liveState?.housekeepingStatus ?? room.housekeepingStatus ?? 'ready',
        serviceState: liveState?.serviceState ?? room.serviceState ?? 'in_service',
    };
}
/**
 * The §3.1 precedence, in order: the service overlay outranks everything
 * because an out-of-order room is not sellable whatever else is true of it;
 * `occupied` outranks housekeeping because a sold room is not sellable either;
 * `ready` is the sellable state; anything else is a housekeeping detail.
 */
export function deriveGrmsDisplayState(axes) {
    if (axes.serviceState === 'out_of_order')
        return 'out_of_order';
    if (axes.serviceState === 'out_of_service')
        return 'out_of_service';
    if (axes.occupancyStatus === 'occupied')
        return 'occupied';
    if (axes.housekeepingStatus === 'ready')
        return 'ready';
    return axes.housekeepingStatus;
}
/** `resolveGrmsRoomAxes` + `deriveGrmsDisplayState` — the common case. */
export function resolveGrmsDisplayState(room, liveState) {
    return deriveGrmsDisplayState(resolveGrmsRoomAxes(room, liveState));
}
/** Collapse the seven-value reading to the five the card frame distinguishes. */
export function toGrmsFrameState(state) {
    switch (state) {
        case 'dirty':
        case 'cleaning':
        case 'to_inspect':
            return 'needs_housekeeping';
        default:
            return state;
    }
}
//# sourceMappingURL=grmsDisplayState.js.map